/**
 * bracketLogic.js — bracket structure and team resolution logic
 * Wiring config (Final Four pairings, Play-In slots) loaded from Firestore via API.
 */

import { useState, useEffect } from 'react';

// ── Static FEEDER map — R64→R32, R32→R16, R16→R8 never change ──────────────
// Game numbering: r64-s1=1v16, r64-s2=8v9, r64-s3=5v12, r64-s4=4v13,
//                 r64-s5=6v11, r64-s6=3v14, r64-s7=7v10, r64-s8=2v15
// R32: r32-s1 = 1v16 winner vs 8v9 winner, r32-s2 = 5v12 vs 4v13, etc.
// R16: r16-s1 = r32-s1 vs r32-s2, r16-s2 = r32-s3 vs r32-s4
// R8:  r8-s   = r16-s1 vs r16-s2
export const STATIC_FEEDER = {
  'r32-s1':['r64-s1','r64-s2'], 'r32-s2':['r64-s3','r64-s4'],
  'r32-s3':['r64-s5','r64-s6'], 'r32-s4':['r64-s7','r64-s8'],
  'r32-e1':['r64-e1','r64-e2'], 'r32-e2':['r64-e3','r64-e4'],
  'r32-e3':['r64-e5','r64-e6'], 'r32-e4':['r64-e7','r64-e8'],
  'r32-m1':['r64-m1','r64-m2'], 'r32-m2':['r64-m3','r64-m4'],
  'r32-m3':['r64-m5','r64-m6'], 'r32-m4':['r64-m7','r64-m8'],
  'r32-w1':['r64-w1','r64-w2'], 'r32-w2':['r64-w3','r64-w4'],
  'r32-w3':['r64-w5','r64-w6'], 'r32-w4':['r64-w7','r64-w8'],
  'r16-s1':['r32-s1','r32-s2'], 'r16-s2':['r32-s3','r32-s4'],
  'r16-e1':['r32-e1','r32-e2'], 'r16-e2':['r32-e3','r32-e4'],
  'r16-m1':['r32-m1','r32-m2'], 'r16-m2':['r32-m3','r32-m4'],
  'r16-w1':['r32-w1','r32-w2'], 'r16-w2':['r32-w3','r32-w4'],
  'r8-s':['r16-s1','r16-s2'], 'r8-e':['r16-e1','r16-e2'],
  'r8-m':['r16-m1','r16-m2'], 'r8-w':['r16-w1','r16-w2'],
  'final-1':['r4-1','r4-2'],
};

const DEFAULT_WIRING = {
  finalFour: { semi1: ['South', 'West'], semi2: ['East', 'Midwest'] },
  regionOrder: ['South', 'East', 'Midwest', 'West'],
  playIn: [],
};

export function buildFeeder(wiring) {
  const ff = (wiring && wiring.finalFour) || DEFAULT_WIRING.finalFour;
  const a = (ff.semi1[0] || 'South')[0].toLowerCase();
  const b = (ff.semi1[1] || 'West')[0].toLowerCase();
  const c = (ff.semi2[0] || 'East')[0].toLowerCase();
  const d = (ff.semi2[1] || 'Midwest')[0].toLowerCase();
  return {
    ...STATIC_FEEDER,
    'r4-1': [`r8-${a}`, `r8-${b}`],
    'r4-2': [`r8-${c}`, `r8-${d}`],
  };
}

// ── Hook: load wiring from /api/wiring (public endpoint, no auth needed) ─────
export function useBracketWiring() {
  const [wiring, setWiring] = useState(DEFAULT_WIRING);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    fetch(`${apiBase}/wiring`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setWiring(data); })
      .catch(() => {}) // silently use defaults on error
      .finally(() => setLoading(false));
  }, []);

  const feeder = buildFeeder(wiring);
  const regionOrder = (wiring && wiring.regionOrder) || DEFAULT_WIRING.regionOrder;
  const overallSeedByRegion = {};
  regionOrder.forEach((r, i) => { overallSeedByRegion[r] = i + 1; });

  return { wiring, feeder, regionOrder, overallSeedByRegion, loading };
}

// ── Team resolution ───────────────────────────────────────────────────────────
const TBD = { id:'tbd', name:'TBD', abbrev:'TBD', seed:99, isTbd:true };

export function resolveAdvancingTeam(gameId, gamesById, allPicks, teamsById) {
  const game = gamesById[gameId];
  if (!game) return null;
  if (game.status === 'final' && game.winner) {
    return teamsById[game.winner]
      || (game.homeTeam?.id === game.winner ? game.homeTeam : game.awayTeam)
      || null;
  }
  const pickedId = allPicks[gameId];
  if (pickedId) {
    return teamsById[pickedId]
      || (game.homeTeam?.id === pickedId ? game.homeTeam : game.awayTeam)
      || null;
  }
  return null;
}

function resolveR64Slot(slotTeam, gamesById, allPicks, teamsById) {
  if (!slotTeam) return TBD;
  if (slotTeam.playInGameId) {
    const playInGame = gamesById[slotTeam.playInGameId];
    if (!playInGame) return { ...TBD, name:'Play-In Winner', seed:slotTeam.seed };
    if (playInGame.status === 'final' && playInGame.winner) {
      const winner = teamsById[playInGame.winner]
        || (playInGame.homeTeam?.id === playInGame.winner ? playInGame.homeTeam : playInGame.awayTeam);
      return winner || TBD;
    }
    const pick = allPicks[slotTeam.playInGameId];
    if (pick) {
      return teamsById[pick]
        || (playInGame.homeTeam?.id === pick ? playInGame.homeTeam : playInGame.awayTeam)
        || TBD;
    }
    return { ...TBD, name: slotTeam.name || 'Play-In Winner', seed:slotTeam.seed, isPlayInPending:true };
  }
  if (slotTeam.id === 'tbd' || !slotTeam.id) return TBD;
  return teamsById[slotTeam.id] || slotTeam;
}

export function getTeamsForGame(gameId, gamesById, allPicks, teamsById, feeder) {
  const game = gamesById[gameId];
  if (!game) return { topTeam:TBD, bottomTeam:TBD };
  const f = feeder || STATIC_FEEDER;
  let team1, team2;
  if (f[gameId]) {
    const [f1, f2] = f[gameId];
    team1 = resolveAdvancingTeam(f1, gamesById, allPicks, teamsById) || TBD;
    team2 = resolveAdvancingTeam(f2, gamesById, allPicks, teamsById) || TBD;
  } else if (game.round === 'R64') {
    team1 = resolveR64Slot(game.homeTeam, gamesById, allPicks, teamsById);
    team2 = resolveR64Slot(game.awayTeam, gamesById, allPicks, teamsById);
  } else {
    team1 = teamsById[game.homeTeam?.id] || game.homeTeam || TBD;
    team2 = teamsById[game.awayTeam?.id] || game.awayTeam || TBD;
  }
  if (team1.isTbd && !team2.isTbd) return { topTeam:team2, bottomTeam:team1 };
  if (team2.isTbd && !team1.isTbd) return { topTeam:team1, bottomTeam:team2 };
  if (team1.isPlayInPending && !team2.isPlayInPending) return { topTeam:team2, bottomTeam:team1 };
  if (team2.isPlayInPending && !team1.isPlayInPending) return { topTeam:team1, bottomTeam:team2 };
  return (team1.seed ?? 99) <= (team2.seed ?? 99)
    ? { topTeam:team1, bottomTeam:team2 }
    : { topTeam:team2, bottomTeam:team1 };
}
